import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Target,
  Heart,
  ShieldCheck,
  Sparkles,
  Users,
  Building2,
  MapPin,
} from "lucide-react";
import Header from "../components/Layout/Header";
import SiteFooter from "../components/Layout/SiteFooter";
import Reveal from "../components/UI/Reveal";
import useSEO from "../hooks/useSEO";

const stats = [
  { value: "25,000+", label: "Active vendors" },
  { value: "1.5L+", label: "Events completed" },
  { value: "500+", label: "Cities covered" },
  { value: "4.9", label: "Average rating" },
];

const values = [
  {
    icon: ShieldCheck,
    title: "Trust first",
    body: "Every vendor is verified so customers can book with confidence, and vendors get paid fairly and on time.",
  },
  {
    icon: Heart,
    title: "Built for celebrations",
    body: "Weddings and events are once-in-a-lifetime moments. We sweat the details so they go off without a hitch.",
  },
  {
    icon: Sparkles,
    title: "Smart by design",
    body: "Our AI engines remove busywork for vendors and make planning effortless for customers.",
  },
  {
    icon: Target,
    title: "Vendor growth",
    body: "We bring vendors a steady stream of qualified leads and the tools to turn them into bookings.",
  },
];

const About = () => {
  useSEO({
    title: "About Us",
    description:
      "Learn how MandapAI is building India's AI-powered event marketplace — connecting customers with verified venues, caterers, photographers and decorators across 500+ cities.",
    path: "/about",
  });
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 sm:pt-20">
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-purple-600" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-600">
              About us
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight leading-[1.1] mb-5">
            We're building India's most trusted event marketplace
          </h1>
          <p className="text-base sm:text-lg text-gray-500 leading-relaxed">
            MandapAI connects people planning weddings and events with
            verified venues, caterers, photographers, and decorators — all in one
            place. We help customers plan with confidence and help vendors grow
            their business.
          </p>
        </Reveal>
      </section>

      {/* Stats */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <Reveal className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <div
              key={i}
              className="rounded-2xl border border-gray-200 bg-gray-50 p-6 text-center"
            >
              <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                {s.value}
              </div>
              <div className="text-sm text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </Reveal>
      </section>

      {/* Mission */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-purple-600" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-600">
                Our mission
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-4">
              Make great events accessible to everyone
            </h2>
            <p className="text-base text-gray-500 leading-relaxed mb-4">
              Planning an event used to mean endless phone calls, unclear pricing,
              and no guarantees. We're changing that with a transparent
              marketplace where quality vendors and discerning customers meet.
            </p>
            <p className="text-base text-gray-500 leading-relaxed">
              For vendors, we're a growth partner — handling discovery, lead
              generation, and the tools to run a modern business.
            </p>
          </Reveal>

          <Reveal delay={120} className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Building2, label: "Venues" },
              { icon: Users, label: "25k+ vendors" },
              { icon: MapPin, label: "500+ cities" },
              { icon: Heart, label: "1.5L+ events" },
            ].map((c, i) => (
              <div
                key={i}
                className="rounded-2xl border border-gray-200 bg-white p-6 flex items-center gap-3"
              >
                <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                  <c.icon className="w-5 h-5" />
                </div>
                <span className="font-semibold text-gray-800 text-sm">
                  {c.label}
                </span>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <Reveal className="max-w-2xl mb-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-purple-600" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-600">
              What we value
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            The principles behind everything we build
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-2 gap-5">
          {values.map((v, i) => (
            <Reveal
              key={i}
              delay={i * 80}
              className="rounded-2xl border border-gray-200 p-6 hover:border-gray-300 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center mb-4">
                <v.icon className="w-5 h-5" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">
                {v.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">{v.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-gray-900 px-6 sm:px-12 py-12 sm:py-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-3">
            Grow your event business with us
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-7">
            Join thousands of vendors reaching new customers every day.
          </p>
          <button
            onClick={() => navigate("/vendor-registration")}
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold text-gray-900 bg-white hover:bg-gray-100 transition-colors duration-300"
          >
            Become a Vendor
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default About;
